import { type FC, useEffect } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';
import { getBuyerId } from 'dibs-cookie-jar';
import { Button } from 'dibs-elements/exports/Button';

import { NftFaq } from 'dibs-nft-faq/exports/NftFaq';

import { HeroBannerCarousel } from './components/HeroBanner';
import { NftExhibitionTileGrid } from './components/NftExhibitionTileGrid';
import { useDeviceType } from '../nftHeader/hooks/useDeviceType';
import { usePageType } from '../nftHeader/hooks/usePageType';
import { FEATURED } from '../nftHeader/pageType';

import { type NftExhibitionLayout_viewer$key } from './__generated__/NftExhibitionLayout_viewer.graphql';
import { type NftExhibitionLayoutRefetchQuery } from './__generated__/NftExhibitionLayoutRefetchQuery.graphql';

import styles from './styles/NftExhibitionLayout.scss';

const messages = defineMessages({
    featuredViewAllHeading: {
        id: 'NftExhibition.NftExhibitionLayout.featuredViewAllHeading',
        defaultMessage: 'Want to see more exhibitions?',
    },
    featuredViewAllButton: {
        id: 'NftExhibition.NftExhibitionLayout.featuredViewAllButton',
        defaultMessage: 'View All Exhibitions',
    },
});

export type NftExhibitionLayoutViewerKey = NftExhibitionLayout_viewer$key;
export const NftExhibitionLayout: FC<{
    isExhibitionLanding: boolean;
    viewer: NftExhibitionLayout_viewer$key | null | undefined;
}> = ({ viewer: viewerProp }) => {
    const { deviceType } = useDeviceType();
    const { formatMessage } = useIntl();
    const pageType = usePageType();
    const isFeatured = pageType === FEATURED;
    const [viewer, refetch] = useRefetchableFragment<
        NftExhibitionLayoutRefetchQuery,
        NftExhibitionLayout_viewer$key
    >(
        graphql`
            fragment NftExhibitionLayout_viewer on Viewer
            @refetchable(queryName: "NftExhibitionLayoutRefetchQuery") {
                ...NftExhibitionTileGrid_viewer
                cmsNftExhibitionPage(urlLabel: $urlLabel, rootId: $pageId) @skip(if: $isFeatured) {
                    status
                    heroBanner {
                        bannerConfig {
                            ... on NftCmsExhibitionHtmlBannerType {
                                eyeBrow
                            }
                        }
                    }
                    ...HeroBannerCarousel_cmsNftExhibitionPage
                    ...NftExhibitionTileGrid_cmsNftExhibitionPage
                }
                cmsNftFeaturedPages @include(if: $isFeatured) {
                    status
                    heroBanner {
                        bannerConfig {
                            ... on NftCmsExhibitionHtmlBannerType {
                                eyeBrow
                            }
                        }
                    }
                    ...HeroBannerCarousel_cmsNftExhibitionPage
                    ...NftExhibitionTileGrid_cmsNftExhibitionPage
                }
            }
        `,
        viewerProp
    );

    useEffect(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            refetch({ userId: buyerId, fetchUser: true });
        }
        addLoginHandlers(() => {
            const userId = getBuyerId(document.cookie);
            if (userId) {
                refetch({ userId: buyerId, fetchUser: true });
            }
        });
    }, [refetch]);

    const featuredExhibitions = viewer?.cmsNftFeaturedPages?.map((exhibition, index) => {
        return (
            <div key={index}>
                <HeroBannerCarousel cmsNftExhibitionPage={exhibition || null} />
                <NftExhibitionTileGrid
                    viewer={viewer || null}
                    cmsNftExhibitionPage={exhibition || null}
                />
            </div>
        );
    });

    return (
        <>
            {isFeatured ? (
                featuredExhibitions
            ) : (
                <>
                    <HeroBannerCarousel
                        cmsNftExhibitionPage={viewer?.cmsNftExhibitionPage || null}
                    />
                    <NftExhibitionTileGrid
                        viewer={viewer || null}
                        cmsNftExhibitionPage={viewer?.cmsNftExhibitionPage || null}
                    />
                </>
            )}
            {isFeatured && (
                <div className={styles.viewAllContainer}>
                    <h2 className={styles.viewAllHeading}>
                        {formatMessage(messages.featuredViewAllHeading)}
                    </h2>
                    <Button
                        type="secondary"
                        size="medium"
                        href="/nft-collections/exhibitions/"
                        className={styles.viewAllButton}
                    >
                        {formatMessage(messages.featuredViewAllButton)}
                    </Button>
                </div>
            )}
            <NftFaq deviceType={deviceType} />
        </>
    );
};
